import { apiService } from '../services/api';

/**
 * "Claim my session" client flow (see the qr-session-claim design):
 * a mid-service anonymous QR guest logs in and carries their live booking/orders/tab/chat onto their
 * registered account. The backend re-points the rows; here we just wire the login handoff.
 */

/** How long a pending claim intent stays valid — an abandoned login shouldn't fire on a later, unrelated sign-in. */
const MAX_INTENT_AGE_MS = 15 * 60 * 1000;

/**
 * Start the flow from a guest surface (QR hub banner / bill nudge): snapshot the guest token BEFORE
 * login clears it, remember where to come back, and send the guest to /login. Returns false (no-op)
 * when this isn't a guest session, so callers can hide the affordance.
 */
export function beginGuestSessionClaim(): boolean {
  const guestToken = apiService.getGuestAccessToken();
  if (!guestToken) return false;
  apiService.setPendingClaim(guestToken);
  // Existing Login.tsx honours this to navigate back after a successful sign-in.
  try { sessionStorage.setItem('redirectAfterLogin', '/customer/bookings'); } catch { /* private mode */ }
  window.location.assign('/login');
  return true;
}

// Module-level guard so the runner performs the claim at most once per load.
let running = false;

/**
 * Run a pending claim once the guest has returned as a registered user. Called by the app-level
 * runner on navigation. On success (or a handled failure) it does a FULL reload into the bookings
 * screen so Ably re-authenticates as the registered user and every screen re-derives its identity.
 */
export async function runPendingGuestClaim(): Promise<void> {
  if (running) return;
  const intent = apiService.getPendingClaim();
  if (!intent) return;
  // Only after login: the guest sessionStorage token is gone and a registered token is present.
  if (apiService.getGuestAccessToken() !== null) return;
  if (!apiService.isAuthenticated()) return;

  running = true;
  // Stale/abandoned intent → drop it silently.
  if (Date.now() - intent.ts > MAX_INTENT_AGE_MS) {
    apiService.clearPendingClaim();
    running = false;
    return;
  }
  // Clear first so a failure can't loop.
  apiService.clearPendingClaim();

  try {
    const res = await apiService.claimGuestSession(intent.guestToken);
    if (res?.success) {
      try { sessionStorage.setItem('qrClaimSuccess', '1'); } catch { /* private mode */ }
    } else {
      try { sessionStorage.setItem('qrClaimError', (res as any)?.message || 'Could not save your visit'); } catch { /* private mode */ }
    }
  } catch (e: any) {
    // 409 collision / expired guest token / session already ended — surface once on the next screen.
    try { sessionStorage.setItem('qrClaimError', e?.message || 'Could not save your visit'); } catch { /* private mode */ }
  }
  // Full reload so realtime + memoized user ids re-point to the registered account.
  window.location.replace('/customer/bookings');
}
